import { MaterialCategory } from '@/models/Materials';

export type PaletteIntendedUse =
	| 'office_commercial'
	| 'healthcare'
	| 'k12_education'
	| 'higher_education'
	| 'residential'
	| 'civic_community'
	| 'laboratory_industrial'
	| 'other';

export type PaletteWellnessIntent =
	| 'code_minimum_baseline'
	| 'above_baseline'
	| 'wellness_focused'
	| 'biophilic_regenerative';

export type PaletteMaterialCharacter =
	| 'predominantly_synthetic'
	| 'mixed_synthetic_natural'
	| 'predominantly_natural'
	| 'exposed_structure_minimal_finishes';

export type PaletteDaylightViewsAssumption =
	| 'limited_daylight_controlled'
	| 'moderate_daylight_access'
	| 'high_daylight_with_glare_control'
	| 'daylight_intentional_views_nature';

export type PaletteAcousticIntent =
	| 'performance_driven_noise_tolerant'
	| 'balanced'
	| 'acoustically_calm_restorative';

export type PaletteDurabilityComfortBias =
	| 'durability_first'
	| 'balanced'
	| 'comfort_experience_first';

export interface PaletteInputs {
	intendedUse: PaletteIntendedUse;
	wellnessIntent: PaletteWellnessIntent;
	materialCharacter: PaletteMaterialCharacter;
	daylightViewsAssumption: PaletteDaylightViewsAssumption;
	acousticIntent: PaletteAcousticIntent;
	durabilityComfortBias: PaletteDurabilityComfortBias;
}

export interface PaletteBand {
	min: number;
	max: number;
}

export interface PaletteMaterialAssumptions {
	wall?: string;
	floor?: string;
	ceiling?: string;
	notes?: string;
}

export interface PaletteScoreBreakdown {
	band: PaletteBand;
	bandMidpoint: number;
	appliedAdjustments: {
		intendedUse: number;
		materialCharacter: number;
		daylightViewsAssumption: number;
		acousticIntent: number;
		durabilityComfortBias: number;
	};
	adjustmentTotal: number;
	rawScore: number;
	clampedScore: number;
	explanation: string;
}

export interface PaletteDefinition {
	id: string;
	name: string;
	inputs: PaletteInputs;
	baselineScore: number;
	band: PaletteBand;
	explanation: string;
	materialAssumptions?: PaletteMaterialAssumptions;
	createdAt: string;
	updatedAt: string;
}

export type WorkflowStage = 'predesign_programming' | 'schematic' | 'design_development';

export type FunctionPaletteAssignments = Record<string, string>;
export type TypologyPaletteAssignments = Record<string, string>;

export interface PaletteState {
	stage: WorkflowStage;
	palettes: PaletteDefinition[];
	functionAssignments: FunctionPaletteAssignments;
	typologyAssignments: TypologyPaletteAssignments;
	updatedAt: string;
}

export type PaletteAssumptionCategory = MaterialCategory;

export const PALETTE_WORKFLOW_SECTION = 8;
export const DEFAULT_WORKFLOW_STAGE: WorkflowStage = 'predesign_programming';

export function createDefaultPaletteState(nowIso: string = new Date().toISOString()): PaletteState {
	return {
		stage: DEFAULT_WORKFLOW_STAGE,
		palettes: [],
		functionAssignments: {},
		typologyAssignments: {},
		updatedAt: nowIso,
	};
}

function isWorkflowStage(value: unknown): value is WorkflowStage {
	return (
		value === 'predesign_programming' ||
		value === 'schematic' ||
		value === 'design_development'
	);
}

export function normalizePaletteState(value: unknown): PaletteState {
	const fallback = createDefaultPaletteState('');

	if (!value || typeof value !== 'object') {
		return fallback;
	}

	const candidate = value as Partial<PaletteState>;
	const stage = isWorkflowStage(candidate.stage) ? candidate.stage : fallback.stage;
	const palettes = Array.isArray(candidate.palettes) ? candidate.palettes : fallback.palettes;
	const functionAssignments =
		candidate.functionAssignments &&
		typeof candidate.functionAssignments === 'object' &&
		!Array.isArray(candidate.functionAssignments)
			? candidate.functionAssignments
			: fallback.functionAssignments;
	const typologyAssignments =
		candidate.typologyAssignments &&
		typeof candidate.typologyAssignments === 'object' &&
		!Array.isArray(candidate.typologyAssignments)
			? candidate.typologyAssignments
			: fallback.typologyAssignments;
	const updatedAt =
		typeof candidate.updatedAt === 'string' && candidate.updatedAt.length > 0
			? candidate.updatedAt
			: fallback.updatedAt;

	return {
		stage,
		palettes,
		functionAssignments,
		typologyAssignments,
		updatedAt,
	};
}
